import { useContext } from 'react'

import {
  Grid,
  Container,
  Divider,
  makeStyles,
  useMediaQuery,
  useTheme,
  ButtonBase,
} from '@material-ui/core'
import dayjs from 'dayjs'

import MailOutlineIcon from '@material-ui/icons/MailOutline'
import PhoneIcon from '@material-ui/icons/Phone'
import ButtonSp from '../ui/ButtonSp'
import clsx from 'clsx'
import Link from '../utility/CustomLink'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { Context } from '../../context'
import useFestivalTheme from '../../utils/hooks/useFestivalTheme'
import { isOldWorld } from '../../utils/isOldWorld'

const useStyles = makeStyles((theme) => ({
  footer: {
    backgroundColor: '#fafafa',
    margin: 0,
    padding: theme.spacing(0),
    paddingTop: theme.spacing(2),
    fontSize: 14,
    '& ul': {
      padding: 0,
    },
    '& li': {
      margin: `${theme.spacing(2)}px 0`,
      listStyleType: 'none',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  copyrightRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    color: '#828282',
    fontSize: 12,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  copyrightRowLinks: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& > *': {
      margin: `0 ${theme.spacing(1)}px`,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  contactUs: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  contactUsRow: {
    width: 'fit-content',
    padding: theme.spacing(1),
    '& > svg': {
      marginRight: theme.spacing(1),
    },
  },
  contactUsRow2: {
    display: 'flex',
    '& > svg': {
      marginRight: theme.spacing(1),
    },
  },
  socialIcons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    '& > *': {
      margin: `0 ${theme.spacing(1)}px`,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  tosAndPrivacy: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    '& > *': {
      margin: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        marginBottom: 0,
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  lineQr: {
    height: 100,
    width: 100,
    margin: 10,
    [theme.breakpoints.down('xs')]: {
      width: 150,
      height: 150,
      marginBottom: theme.spacing(7.5),
    },
  },
  button: {
    ...theme.homeStyle.button,
  },
  mobileAppButton: {
    textTransform: 'none',
    fontSize: 14,
    lineHeight: '18px',
  },
  mobileCopyrightText: {
    textAlign: 'center',
    color: 'black',
    margin: theme.spacing(1),
  },
  lineDiv: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `${theme.spacing(1)}px 0`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      alignItems: 'center',
    },
  },
  lineButton: {
    width: 200,
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(1),
    },
  },
  clickableText: {
    display: 'contents',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  }
}))

const NewAboutUs = ({ t }) => {
  return (
    <>
      <p style={{ maxWidth: 300, margin: 0 }}>
        <b>{t('address-1')}</b>
      </p>
      <p style={{ maxWidth: 300, margin: 0 }}>{t('address-2')}</p>
      <p style={{ maxWidth: 300, margin: 0 }}>{t('address-3')}</p>
      <p style={{ maxWidth: 300, margin: 0 }}>{t('address-4')}</p>
      <p style={{ maxWidth: 300, margin: 0 }}>{t('address-5')}</p>
    </>
  )
}

const Footer = ({ handleClickSetCookies }) => {
  const theme = useTheme()

  const { asset, logoStyle } = useFestivalTheme()

  const { isFromApp } = useContext(Context)
  const addQueryParam = isFromApp ? '?shippop_app' : ''

  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const { t } = useTranslation('footer')
  const Router = useRouter()

  // This is the OLD shared Footer — keep internal nav in the old world by
  // appending `old` to link targets whenever the current route is old-world.
  const toOld = (href) =>
    isOldWorld(Router) ? `${href}${href.includes('?') ? '&' : '?'}old` : href

  return (
    <div className={classes.footer}>
      <Container>
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            md={3}
            style={{
              textAlign: isTabletOrMobile ? 'center' : 'left',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isTabletOrMobile ? 'center' : 'flex-start',
            }}
          >
            <img
              src={asset.logo}
              alt="SHIPPOP"
              className={logoStyle.footer.className}
            />
            {/* <p style={{ maxWidth: 300 }}>{t('address')}</p> */}
            <NewAboutUs t={t} />
            {/*<p>{t('tax-id-no')}</p>*/}
            <Link
              href={toOld('/mobile-app')}
              style={{ width: isTabletOrMobile ? '100%' : 'auto' }}
              passHref
            >
              <ButtonSp
                className={clsx(classes.button, classes.mobileAppButton)}
                component="a"
              >
                {t('download-app')}
              </ButtonSp>
            </Link>
          </Grid>
          <Grid item xs={6} md={3}>
            <h3>{t('menu')}</h3>
            <ul>
              <Link href={toOld(`/${addQueryParam}`)} passHref>
                <a title={t('home')}>
                  <li>{t('home')}</li>
                </a>
              </Link>
              <Link href={toOld(`/service${addQueryParam}`)} passHref>
                <a title={t('shippop-services')}>
                  <li>{t('shippop-services')}</li>
                </a>
              </Link>
              <Link href={toOld(`/for-developers${addQueryParam}`)} passHref>
                <a title={t('for-developers')}>
                  <li>{t('for-developers')}</li>
                </a>
              </Link>
              <Link href={toOld(`/pro${addQueryParam}`)} passHref>
                <a title={t('shippop-pro')}>
                  <li>{t('shippop-pro')}</li>
                </a>
              </Link>
              <Link href={toOld(`/about${addQueryParam}`)} passHref>
                <a title={t('about-us')}>
                  <li>{t('about-us')}</li>
                </a>
              </Link>
              <li>
                <a
                  href="https://blog.shippop.com"
                  target="_blank"
                  title={t('news')}
                >
                  {t('news')}
                </a>
              </li>
              <li>
                <a
                  href="https://blog.shippop.com/category/%E0%B8%9B%E0%B8%A3%E0%B8%B0%E0%B8%81%E0%B8%B2%E0%B8%A8/"
                  target="_blank"
                  title={t('announcement')}
                >
                  {t('announcement')}
                </a>
              </li>
              <Link href={toOld(`/jobs${addQueryParam}`)} passHref title={t('career')}>
                <a title={t('career')}>
                  <li>{t('career')}</li>
                </a>
              </Link>
            </ul>
          </Grid>
          <Grid item xs={6} md={3}>
            <h3>{t('help')}</h3>
            <ul>
              <Link href={toOld(`/faq${addQueryParam}`)} passHref>
                <a title={`${t('faq')}(${t('domestic')})`}>
                  <li>
                    {`${t('faq')}`}
                    {isMobile && Router.locale === 'th' ? <br /> : ' '}
                    {`(${t('domestic')})`}
                  </li>
                </a>
              </Link>
              <Link href={toOld(`/inter/faq${addQueryParam}`)} passHref>
                <a title={`${t('faq')}(${t('inter')})`}>
                  <li>
                    {`${t('faq')}`}
                    {isMobile && Router.locale === 'th' ? <br /> : ' '}
                    {`(${t('inter')})`}
                  </li>
                </a>
              </Link>
              <Link href={toOld(`/nearby-dropoff${addQueryParam}`)} passHref>
                <a title={t('dropoff')}>
                  <li>{t('dropoff')}</li>
                </a>
              </Link>
              <Link href={toOld(`/tracking${addQueryParam}`)} passHref>
                <a title={t('tracking')}>
                  <li>{t('tracking')}</li>
                </a>
              </Link>
              <Link href={toOld(`/contact${addQueryParam}`)} passHref title={t('contact-us')}>
                <a>
                  <li>{t('contact-us')}</li>
                </a>
              </Link>
              <Link href={toOld(`/widget${addQueryParam}`)} passHref title={t('widgets')}>
                <a>
                  <li>{t('widgets')}</li>
                </a>
              </Link>
            </ul>
          </Grid>
          <Grid item xs={12} md={3} className={classes.contactUs}>
            <h3>{t('contact-us')}</h3>
            <Grid container direction="row" className={classes.contactUsRow}>
              <div className={classes.contactUsRow2}>
                <PhoneIcon />
                <div>
                  <a className={classes.clickableText} href="tel:+6620966629"> (+66) 2-096-6629</a>
                  {true ? <br /> : ' '}
                  <p style={{ margin: 0 }}>{t('business-time-1')}</p>
                  {/*<p style={{ margin: 0 }}>{t('business-time-2')}</p>*/}
                </div>
              </div>
            </Grid>
            <Grid container direction="row" className={classes.contactUsRow}>
              <MailOutlineIcon />
              <a className={classes.clickableText} href="mailto:cs@shippop.com"> cs@shippop.com </a>
            </Grid>
            <div className={classes.lineDiv}>
              {/* <span style={{ marginTop: 12 }}>
                @shippop {t('have-at-sign')}
              </span> */}
              <h3 style={{ marginTop: 8 }}>{t('add-friend-line')}</h3>
              <ButtonBase
                focusRipple
                component="a"
                href="https://line.me/R/ti/p/@shippop"
                target="_blank"
                className={classes.lineButton}
              >
                <img
                  src="/static/img/icons/line_add_friend_button.svg"
                  alt="Add Friend SHIPPOP"
                  style={{ width: 200 }}
                />
              </ButtonBase>
            </div>
          </Grid>
        </Grid>
        {!isTabletOrMobile && <Divider style={{ margin: '10px 0px' }} />}
        <div className={classes.copyrightRow}>
          {!isTabletOrMobile && (
            <p style={{ textAlign: 'center' }}>
              © Copyright 2015 - {dayjs().year()} All Rights Reserved by SHIPPOP
            </p>
          )}
          <div className={classes.copyrightRowLinks}>
            <div className={classes.tosAndPrivacy}>
              <Link href={toOld(`/terms${addQueryParam}`)} passHref>
                <a className={classes.cursorPointer} title={t('tos')}>
                  {t('tos')}
                </a>
              </Link>
              <Link href={toOld(`/privacy${addQueryParam}`)} passHref>
                <a className={classes.cursorPointer} title={t('privacy')}>
                  {t('privacy')}
                </a>
              </Link>
              <span
                className={classes.cursorPointer}
                onClick={handleClickSetCookies}
              >
                {t('cookies-setting')}
              </span>
            </div>
            {isTabletOrMobile && (
              <p className={classes.mobileCopyrightText}>
                © Copyright 2015 - {dayjs().year()} All Rights Reserved by
                SHIPPOP
              </p>
            )}
            <div className={classes.socialIcons}>
              <a
                href="https://www.facebook.com/shippop.official"
                target="_blank"
                rel="noreferrer"
                title="SHIPPOP Facebook page"
              >
                <img
                  src="/static/img/icons/facebook.svg"
                  alt="facebook"
                  width={20}
                  height={20}
                />
              </a>
              <a
                href="https://www.youtube.com/channel/UCqthHHdPcpFlI8gTA6TAOdw"
                target="_blank"
                rel="noreferrer"
                title="SHIPPOP Youtube channel"
              >
                <img
                  src="/static/img/icons/youtube.svg"
                  alt="youtube"
                  width={20}
                  height={20}
                />
              </a>
              <a
                href="https://instagram.com/shippopcom"
                target="_blank"
                rel="noreferrer"
                title="SHIPPOP Instagram"
              >
                <img
                  src="/static/img/icons/instagram.svg"
                  alt="instagram"
                  width={20}
                  height={20}
                />
              </a>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default Footer
